import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ExternalLink } from 'lucide-react';
import {
  parseProductFieldLabel,
  parseStructuredProfileValue,
} from '@/lib/ispProfileRender';

const CONFIDENCE_COLORS = {
  H: 'bg-green-100 text-green-800 border-green-200',
  M: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  L: 'bg-red-100 text-red-800 border-red-200',
};

const CATEGORY_CHIP_CLASS = {
  internet: 'bg-sky-50 text-sky-700 border-sky-200',
  tv: 'bg-violet-50 text-violet-700 border-violet-200',
  voice: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  phone: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  security: 'bg-amber-50 text-amber-800 border-amber-200',
  business: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  mobile: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  other: 'bg-gray-50 text-gray-600 border-gray-200',
};

export function getConfidenceBadge(confidence, t) {
  const labels = {
    H: t('high'),
    M: t('medium'),
    L: t('low'),
  };

  return (
    <Badge className={`${CONFIDENCE_COLORS[confidence] || CONFIDENCE_COLORS.M} text-xs border`}>
      {labels[confidence] || labels.M}
    </Badge>
  );
}

export function renderSource(source, url) {
  if (!source || source === '—') return '—';

  if (url && url !== '—') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#00C2C2] hover:text-[#00A3A3] text-sm inline-flex items-center gap-1"
      >
        {source}
        <ExternalLink className="w-3 h-3" />
      </a>
    );
  }

  return <span className="text-sm">{source}</span>;
}

export function ProfileValueContent({ value }) {
  const structured = parseStructuredProfileValue(value);

  if (!structured) {
    return <span>{value == null || value === '' ? '—' : String(value)}</span>;
  }

  return (
    <div className="space-y-2">
      {structured.lead ? (
        <p className="text-sm text-gray-700 leading-relaxed">{structured.lead}</p>
      ) : null}
      {structured.bullets.length > 0 ? (
        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
          {structured.bullets.map((bullet, index) => (
            <li key={index}>{bullet}</li>
          ))}
        </ul>
      ) : null}
      {structured.feesTerms ? (
        <p className="text-xs text-[#5C6B7A]/80 leading-relaxed">{structured.feesTerms}</p>
      ) : null}
    </div>
  );
}

export function ProfileFieldLabel({ field }) {
  const { label, category } = parseProductFieldLabel(field);
  const chipClass = category
    ? CATEGORY_CHIP_CLASS[category] || CATEGORY_CHIP_CLASS.other
    : null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span>{label}</span>
      {category ? (
        <Badge variant="outline" className={`text-[10px] uppercase tracking-wide px-1.5 py-0 ${chipClass}`}>
          {category}
        </Badge>
      ) : null}
    </div>
  );
}

export function ProfileDataSection({ title, data, t }) {
  return (
    <div className="mb-6">
      <h4 className="font-semibold text-[#0A1A2F] mb-3 text-sm md:text-base">{title}</h4>

      <div className="hidden md:block border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-xs font-medium text-left w-1/4">{t('fieldMetric')}</TableHead>
              <TableHead className="text-xs font-medium text-left w-2/5">{t('value')}</TableHead>
              <TableHead className="text-xs font-medium text-left w-1/5">{t('source')}</TableHead>
              <TableHead className="text-xs font-medium text-center w-20">{t('confidence')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index} className="hover:bg-gray-50">
                <TableCell className="font-medium text-sm text-left align-top py-3">
                  <ProfileFieldLabel field={row.field} />
                </TableCell>
                <TableCell className="text-sm text-left align-top py-3">
                  <ProfileValueContent value={row.value} />
                </TableCell>
                <TableCell className="text-left align-top py-3">
                  {renderSource(row.source, row.url)}
                </TableCell>
                <TableCell className="text-center align-top py-3">
                  {getConfidenceBadge(row.confidence, t)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="md:hidden space-y-3">
        {data.map((row, index) => (
          <Card key={index} className="border border-gray-200">
            <CardContent className="p-4">
              <div className="space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <div className="font-medium text-sm text-[#0A1A2F] flex-1">
                    <ProfileFieldLabel field={row.field} />
                  </div>
                  {getConfidenceBadge(row.confidence, t)}
                </div>
                <div className="text-sm text-gray-700">
                  <ProfileValueContent value={row.value} />
                </div>
                {row.source && row.source !== '—' ? (
                  <div className="pt-1">
                    <span className="text-xs text-gray-500">{t('source')}: </span>
                    {renderSource(row.source, row.url)}
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
